import {useState, useContext, useCallback, useEffect, useMemo, useRef} from 'react';
import moment from 'moment';

import Context from 'utils/Context';

import usePersistentState from './usePersistentState';
import useViewingStats from './useViewingStats';

const INTRO_DURATION = 13;
const LIGHTS_OFF_DURATION = 8;
const INTER_VIDEO_DURATION = 3;
const FILM_CREDITS_MEAN_DURATION = 30;
const TIMECODE_STEP = 2;

const getSeanceStartAt = (startAt, playlist, {appState}) => {
  if (!playlist.some(item => item.name === 'live')) {
    return moment(startAt);
  }
  const actualPreRollDuration = playlist.reduce((total, item) => {
    if (['jingle', 'global_trailer', 'seance_trailer'].find(name => item.name === name)) {
      return total + item.duration;
    }
    return total;
  }, 0);

  return moment(startAt)
    .add(appState.conf.AVANT_SEANCE, 'minutes')
    .subtract(actualPreRollDuration, 'seconds');
};

export const getPropertiesFromTimecode = (timecode, currentPlaylistItem, retardMax) => {
  const idleProperties = {
    chattingAvailable: false,
    lightsOff: false,
    screenOn: false,
    filmEnded: false,
    seanceStatus: 'waiting',
  };
  // until 1 hour before seance start, stay idle
  if (timecode < -3600) {
    return idleProperties;
  }

  // 1 hour before seance start to few seconds before intro, only chat is available
  if (timecode < -(INTRO_DURATION + LIGHTS_OFF_DURATION)) {
    return {
      ...idleProperties,
      chattingAvailable: true,
    };
  }
  // few seconds before intro, lights turns off
  if (timecode < -INTRO_DURATION) {
    return {
      ...idleProperties,
      chattingAvailable: true,
      lightsOff: true,
    };
  }

  if (currentPlaylistItem) {
    return {
      ...idleProperties,
      chattingAvailable: true,
      lightsOff: true,
      screenOn: true,
      seanceStatus: 'playing',
    };
  }
  return {
    ...idleProperties,
    chattingAvailable: false,
    lightsOff: false,
    screenOn: false,
    seanceStatus: 'ended',
  };
};

const getPlaylist = seance => {
  const playlist = [
    {
      name: 'intro',
      startTimecode: -INTRO_DURATION,
      duration: INTRO_DURATION,
    },
  ];

  let currentTimecode = 0;
  if (seance.jingle_id) {
    playlist.push({
      name: 'jingle',
      vimdeoId: seance.jingle_id,
      startTimecode: 0,
      duration: seance.cinemaJingleDuration + INTER_VIDEO_DURATION,
    });
    currentTimecode += seance.cinemaJingleDuration + INTER_VIDEO_DURATION;
  }
  if (seance.bande_annonce_admin) {
    playlist.push({
      name: 'global_trailer',
      vimeoId: seance.bande_annonce_admin,
      startTimecode: currentTimecode,
      duration: seance.globalTrailerDuration + INTER_VIDEO_DURATION,
    });
    currentTimecode += seance.globalTrailerDuration + INTER_VIDEO_DURATION;
  }
  if (seance.intro_id) {
    playlist.push({
      name: 'seance_trailer',
      vimeoId: seance.intro_id,
      startTimecode: currentTimecode,
      duration: seance.seanceTrailerDuration + INTER_VIDEO_DURATION,
    });
    currentTimecode += seance.seanceTrailerDuration + INTER_VIDEO_DURATION;
  }
  if (seance.is_aws_video) {
    playlist.push({
      name: 'film',
      src: seance.media_video_url,
      startTimecode: currentTimecode,
      duration: seance.media_video_duration + INTER_VIDEO_DURATION,
    });
    currentTimecode += seance.media_video_duration + INTER_VIDEO_DURATION;
  } else {
    playlist.push({
      name: 'film',
      vimdeoId: seance.film_id,
      startTimecode: currentTimecode,
      duration: seance.media_video_duration + INTER_VIDEO_DURATION,
    });
    currentTimecode += seance.media_video_duration + INTER_VIDEO_DURATION;
  }
  if (seance.event_id) {
    playlist.push({
      name: 'live',
      vimeoId: seance.live_id,
      startTimecode: currentTimecode,
      duration: 864000, // 4 hours
    });
  }
  return playlist;
};

const getPlaylistItem = (playlist, timecode) => {
  // for (let index = playlist.length - 1; index >= 0; index--) {
  //   const item = playlist[index];
  //   if (timecode >= item.startTimecode) {
  //     return item;
  //   }
  // }
  // return null;
  return playlist.find(item => timecode >= item.startTimecode && timecode < item.startTimecode + item.duration);
};

const useViewerTimings = seance => {
  // timeCode is the number of seconds from seanceStart
  // So, seanceTimecode is the live timecode
  // and userTimecode is the viewer personnal playback state
  const {appState} = useContext(Context);
  const [personalTimecode, setPersonalTimecode] = usePersistentState(`seance-${seance.seance_id}-timecode`, null);
  const [watchingLive, setWatchingLive] = usePersistentState(`seance-${seance.seance_id}-live`, false);

  const handlePlayerTimeUpdate = useViewingStats(seance);

  const [startTimecode, setStartTimecode] = useState(0);
  const [paused, setPaused] = useState(false);

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // const cinemaJingleDuration = useVimeoDuration(seance.jingle_id);
  // const globalTrailerDuration = useVimeoDuration(seance.bande_annonce_admin);
  // const seanceTrailerDuration = useVimeoDuration(seance.intro_id);
  // const filmDuration = useVimeoDuration(seance.film_id);

  const playlist = useMemo(() => getPlaylist(seance), [seance]);
  const seanceStartAt = getSeanceStartAt(seance.startAt, playlist, {appState});
  const seanceTimecode = moment(now).diff(seanceStartAt, 'seconds');
  const usedTimecode = !personalTimecode || watchingLive ? seanceTimecode : personalTimecode;
  const seancePlaylistItem = getPlaylistItem(playlist, seanceTimecode);
  const personalPlaylistItem = getPlaylistItem(playlist, personalTimecode);
  const film = playlist.find(p => p.name === 'film');

  useEffect(() => {
    // When playback is delayed and reach max delay
    const delay = personalTimecode ? seanceTimecode - personalTimecode : 0;
    const maxDelay = Math.max(
      appState.conf.RETARD_MAX * 60,
      seance.event_id
        ? seanceTimecode - (film.startTimecode + film.media_video_duration - appState.conf.RETARD_MAX * 60)
        : 0,
    );
    if (delay >= maxDelay) {
      const newTimecode = seanceTimecode - maxDelay + TIMECODE_STEP;
      setPersonalTimecode(newTimecode);
      setStartTimecode(newTimecode - currentPlaylistItemStartTimecode.current);
      setPaused(false);
      return;
    }
    if (delay < 0) {
      setPersonalTimecode(seanceTimecode);
      setStartTimecode(seanceTimecode - currentPlaylistItemStartTimecode.current);
      return;
    }
  }, [personalTimecode, appState.conf, seanceTimecode, setPersonalTimecode, film, seance.event_id]);

  const currentPlaylistItem = !personalTimecode || watchingLive ? seancePlaylistItem : personalPlaylistItem;

  const currentPlaylistItemStartTimecode = useRef(currentPlaylistItem ? currentPlaylistItem.startTimecode : null);
  const currentPlaylistItemName = useRef(currentPlaylistItem ? currentPlaylistItem.name : null);
  useEffect(() => {
    if (!currentPlaylistItem) return;
    currentPlaylistItemStartTimecode.current = currentPlaylistItem.startTimecode;
    currentPlaylistItemName.current = currentPlaylistItem.name;
  }, [currentPlaylistItem]);

  useEffect(() => {
    // Only run for initialization
    if (startTimecode) return;

    // If a viewing timecode is saved, then use it
    if (personalTimecode && personalPlaylistItem) {
      setStartTimecode(personalTimecode - personalPlaylistItem.startTimecode);
      return;
    }

    // If seance has not started, wait for start
    if (!seancePlaylistItem) return;

    if (seancePlaylistItem.name === 'live') {
      // Else if current is live, set the start at earliest viewable timecode of the film
      setStartTimecode(Math.max(0, seance.media_video_duration - appState.conf.RETARD_MAX * 60));
      return;
    }
    // Else start is seance timecode
    setStartTimecode(seanceTimecode - seancePlaylistItem.startTimecode);
    return;
  }, [
    seancePlaylistItem,
    personalPlaylistItem,
    startTimecode,
    seanceTimecode,
    personalTimecode,
    seance.media_video_duration,
    appState.conf.RETARD_MAX,
  ]);

  const currentVideoTimecode = currentPlaylistItem ? usedTimecode - currentPlaylistItem.startTimecode : 0;
  const seanceCurrentVideoTimecode = currentPlaylistItem ? seanceTimecode - currentPlaylistItem.startTimecode : 0;
  const min =
    (seance.event_id ? Math.min(seance.media_video_duration, seanceCurrentVideoTimecode) : seanceCurrentVideoTimecode) -
    appState.conf.RETARD_MAX * 60;
  const max = seanceCurrentVideoTimecode;

  const onEndCurrentVideo = useCallback(() => {
    if (watchingLive) return;
    const videoEndTimecode = currentPlaylistItem.startTimecode + currentPlaylistItem.duration;
    const nextVideo = getPlaylistItem(playlist, videoEndTimecode);
    if (nextVideo && nextVideo.name === 'live') {
      setWatchingLive(true);
    }
    console.log('currentVideo Ends ???', {playlist, videoEndTimecode});
    setStartTimecode(0);
    setPersonalTimecode(videoEndTimecode);
  }, [currentPlaylistItem, watchingLive, setPersonalTimecode, playlist, setWatchingLive]);

  const onPlayerTimeUpdate = useCallback(
    currentPlayingVideoTimecode => {
      if (currentPlaylistItemName.current === 'film') {
        handlePlayerTimeUpdate(currentPlayingVideoTimecode);
      }
      setPersonalTimecode(currentPlaylistItemStartTimecode.current + currentPlayingVideoTimecode);
    },
    [handlePlayerTimeUpdate, setPersonalTimecode],
  );

  const onChangeTimecode = useCallback(timecode => {
    setStartTimecode(timecode);
  }, []);

  const toggleWatchLive = value => {
    setWatchingLive(value);
    if (!personalTimecode) {
      // Set the start at earliest viewable timecode of the film
      setPersonalTimecode(
        currentPlaylistItem.startTimecode - Math.min(seance.media_video_duration, appState.conf.RETARD_MAX * 60),
      );
    }
  };

  const onTogglePlay = useCallback(playing => {
    setPaused(!playing);
  }, []);

  const roomProperties = getPropertiesFromTimecode(seanceTimecode, currentPlaylistItem, appState.conf.RETARD_MAX);

  console.log('HERE', {startTimecode, currentVideoTimecode, currentPlaylistItem});
  const currentVideo = currentPlaylistItem
    ? {
        ...currentPlaylistItem,
        currentTimecode: currentVideoTimecode,
        videoStartTimecode: !watchingLive ? startTimecode || currentVideoTimecode : currentVideoTimecode,
      }
    : null;

  const filmViewedTimecode = film && personalTimecode ? personalTimecode - film.startTimecode : 0;

  const canResumeFilm =
    seancePlaylistItem &&
    seancePlaylistItem.name === 'live' &&
    filmViewedTimecode < film.duration - FILM_CREDITS_MEAN_DURATION;

  return {
    ...roomProperties,
    seanceStartAt,
    currentVideo,
    liveStarted: seancePlaylistItem && seancePlaylistItem.name === 'live',
    canResumeFilm,
    min,
    max,
    onEndCurrentVideo,
    onPlayerTimeUpdate,
    onChangeTimecode,
    toggleWatchLive,
    onTogglePlay,
    paused,
  };
};

export default useViewerTimings;
